import { useState, useEffect } from 'react';
import { api } from '../utils/api';
import LogoUpload from './LogoUpload';

const ZipcodeForm = () => {
  const [zipCode, setZipCode] = useState('');
  const [country, setCountry] = useState('USA');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [originalLatitude, setOriginalLatitude] = useState(''); // Track original coordinates
  const [originalLongitude, setOriginalLongitude] = useState(''); // Track original coordinates
  const [language, setLanguage] = useState('he');
  const [timeFormat, setTimeFormat] = useState('12h');
  const [showSeconds, setShowSeconds] = useState(false);
  const [showAmPm, setShowAmPm] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoadingCoordinates, setIsLoadingCoordinates] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [currentZipCode, setCurrentZipCode] = useState('');

  // Styles
  const styles = {
    container: {
      maxWidth: '900px',
      margin: '0 auto',
      padding: '0',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      backgroundColor: 'transparent'
    },
    header: {
      marginBottom: '2.5rem',
      textAlign: 'left',
      padding: '2rem',
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      border: '1px solid #e5e7eb'
    },
    title: {
      fontSize: '2rem',
      fontWeight: '700',
      margin: '0 0 0.5rem 0',
      color: '#0f172a'
    },
    subtitle: {
      fontSize: '1rem',
      color: '#6b7280',
      margin: 0
    },
    currentInfo: {
      padding: '1.25rem 2rem',
      marginBottom: '2rem',
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      fontSize: '0.95rem',
      color: '#374151',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    section: {
      marginBottom: '2rem',
      padding: '2.5rem',
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
    },
    sectionTitle: {
      fontSize: '1.4rem',
      fontWeight: '600',
      color: '#0f172a',
      marginBottom: '0.75rem',
      paddingBottom: '0',
      borderBottom: 'none'
    },
    sectionDescription: {
      fontSize: '0.9rem',
      color: '#6b7280',
      marginBottom: '1.75rem',
      lineHeight: '1.6'
    },
    sectionDivider: {
      height: '1px',
      backgroundColor: '#e5e7eb',
      margin: '2rem 0',
      border: 'none'
    },
    subsectionTitle: {
      fontSize: '1.05rem',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '1rem',
      marginTop: '1.5rem'
    },
    formGroup: {
      marginBottom: '1.5rem'
    },
    label: {
      display: 'block',
      fontSize: '0.95rem',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '0.5rem'
    },
    input: {
      width: '100%',
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '1rem',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      backgroundColor: '#ffffff',
      boxSizing: 'border-box'
    },
    inputFocus: {
      outline: 'none',
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
    },
    select: {
      width: '100%',
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '1rem',
      backgroundColor: '#ffffff',
      cursor: 'pointer',
      transition: 'border-color 0.2s',
      boxSizing: 'border-box'
    },
    flexRow: {
      display: 'flex',
      gap: '1rem',
      alignItems: 'end'
    },
    flexEqual: {
      flex: 1
    },
    button: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: '0.95rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s',
      whiteSpace: 'nowrap'
    },
    buttonHover: {
      backgroundColor: '#2563eb'
    },
    buttonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed'
    },
    primaryButton: {
      padding: '1.25rem 2rem',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontSize: '1.05rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
      marginTop: '0',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      boxShadow: '0 2px 4px rgba(59,130,246,0.2)'
    },
    alert: {
      padding: '1.25rem 2rem',
      marginTop: '2rem',
      borderRadius: '12px',
      fontSize: '0.95rem',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem'
    },
    alertSuccess: {
      backgroundColor: '#d1fae5',
      border: '1px solid #a7f3d0',
      color: '#065f46',
      boxShadow: '0 1px 3px rgba(16,185,129,0.1)'
    },
    alertError: {
      backgroundColor: '#fee2e2',
      border: '1px solid #fecaca',
      color: '#991b1b',
      boxShadow: '0 1px 3px rgba(239,68,68,0.1)'
    },
    gridTwo: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1rem'
    },
    gridThree: {
      display: 'grid',
      gridTemplateColumns: '2fr 1fr 1fr',
      gap: '1rem'
    },
    helpText: {
      fontSize: '0.85rem',
      color: '#6b7280',
      marginTop: '0.25rem'
    },
    spinner: {
      width: '1rem',
      height: '1rem',
      border: '2px solid #ffffff',
      borderTop: '2px solid transparent',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }
  };

  // Load current settings when component mounts
  useEffect(() => {
    const loadCurrentSettings = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      try {
        const data = await api.get('/shul/');
        if (data.zip_code) {
          setCurrentZipCode(data.zip_code);
          setZipCode(data.zip_code); // Also populate the input field
        }
        if (data.country) setCountry(data.country);
        if (data.latitude) {
          setLatitude(data.latitude.toString());
          setOriginalLatitude(data.latitude.toString());
        }
        if (data.longitude) {
          setLongitude(data.longitude.toString());
          setOriginalLongitude(data.longitude.toString());
        }
        if (data.language) setLanguage(data.language);
        if (data.time_format) setTimeFormat(data.time_format);
        if (data.show_seconds !== undefined) setShowSeconds(data.show_seconds);
        if (data.show_am_pm !== undefined) setShowAmPm(data.show_am_pm);
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };

    loadCurrentSettings();
  }, []);

  // Country mapping for geocoding
  const getCountryName = (code) => {
    const countryMap = {
      'USA': 'United States', 'ISR': 'Israel', 'CAN': 'Canada', 'GBR': 'United Kingdom',
      'AUS': 'Australia', 'ZAF': 'South Africa', 'FRA': 'France', 'BRA': 'Brazil',
      'ARG': 'Argentina', 'RUS': 'Russia', 'MEX': 'Mexico', 'GER': 'Germany',
      'NLD': 'Netherlands', 'BEL': 'Belgium', 'ESP': 'Spain', 'ITA': 'Italy',
      'CHL': 'Chile', 'UKR': 'Ukraine', 'HUN': 'Hungary', 'AUT': 'Austria'
    };
    return countryMap[code] || code;
  };

  // Fetch coordinates from zip code and auto-save
  const fetchCoordinates = async () => {
    if (!zipCode || zipCode.length < 3) return;
    
    setIsLoadingCoordinates(true);
    setSuccessMessage('');
    
    try {
      const query = country === 'USA' ? `${zipCode}, United States` : `${zipCode}, ${getCountryName(country)}`;
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data && data.length > 0) {
        const result = data[0];
        const newLat = parseFloat(result.lat).toFixed(6);
        const newLon = parseFloat(result.lon).toFixed(6);
        
        setLatitude(newLat);
        setLongitude(newLon);
        
        // Auto-save the settings with new coordinates
        await saveSettings(newLat, newLon);
        
      } else {
        setSuccessMessage('Could not find coordinates for this location');
      }
    } catch (error) {
      setSuccessMessage('Error fetching coordinates');
      console.error('Geocoding error:', error);
    } finally {
      setIsLoadingCoordinates(false);
    }
  };

  // Helper function to save settings
  const saveSettings = async (lat = latitude, lon = longitude) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setSuccessMessage('Error: No authentication token found. Please log in.');
      return;
    }

    // Check if coordinates are being updated (which will trigger zmanim recalculation)
    const coordinatesChanged = lat && lon && originalLatitude && originalLongitude && (
      parseFloat(lat) !== parseFloat(originalLatitude) ||
      parseFloat(lon) !== parseFloat(originalLongitude)
    );

    // Use FormData for file upload support
    const formData = new FormData();
    formData.append('language', language);
    formData.append('time_format', timeFormat);
    formData.append('show_seconds', showSeconds);
    formData.append('show_am_pm', showAmPm);

    // Include location data if coordinates are available
    if (lat && lon) {
      formData.append('zip_code', zipCode);
      formData.append('country', country);
      formData.append('latitude', parseFloat(lat));
      formData.append('longitude', parseFloat(lon));
    }

    try {
      if (coordinatesChanged) {
        setSuccessMessage('Saving settings and recalculating zmanim for the next 6 months. This may take up to 2 minutes...');
      } else {
        setSuccessMessage('Saving settings...');
      }

      // Use fetch directly for FormData upload
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api'}/shul/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to save settings');
      }

      if (coordinatesChanged) {
        setSuccessMessage('Settings saved successfully! Zmanim are being recalculated in the background. This may take up to 2 minutes to complete.');
        // Update the original coordinates to the new values
        setOriginalLatitude(lat);
        setOriginalLongitude(lon);
        setTimeout(() => setSuccessMessage(''), 8000); // Show longer for coordinate changes
      } else {
        setSuccessMessage('Settings saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      }

    } catch (error) {
      console.error('Error saving settings:', error);
      setSuccessMessage('Failed to save settings. Please try again.');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    await saveSettings();
    setIsSaving(false);
  };

  return (
    <div style={styles.container}>
      <style>
        {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
      </style>
      
      <div style={styles.header}>
        <h1 style={styles.title}>Shul Configuration</h1>
        <p style={styles.subtitle}>Configure your shul's location and display preferences</p>
      </div>

      {currentZipCode && (
        <div style={styles.currentInfo}>
          <strong>Current Location:</strong> {currentZipCode}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* ========== SECTION 1: BASIC CONFIGURATION ========== */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Basic Configuration</h2>
          <p style={styles.sectionDescription}>
            Set up your shul's location and regional preferences. Location is required for accurate zmanim calculations.
          </p>

          <h3 style={styles.subsectionTitle}>Location Information</h3>

          <div style={styles.formGroup}>
            <label style={styles.label}>Zip/Postal Code</label>
            <div style={styles.flexRow}>
              <input
                type="text"
                value={zipCode}
                onChange={e => setZipCode(e.target.value)}
                placeholder="Enter zip code (e.g., 10001)"
                style={{ ...styles.input, ...styles.flexEqual }}
              />
              <button 
                type="button" 
                onClick={fetchCoordinates}
                disabled={zipCode.length < 3 || isLoadingCoordinates}
                style={{
                  ...styles.button,
                  ...(zipCode.length < 3 || isLoadingCoordinates ? styles.buttonDisabled : {})
                }}
              >
                {isLoadingCoordinates ? (
                  <>
                    <div style={styles.spinner}></div>
                    Loading...
                  </>
                ) : (
                  'Get Coordinates'
                )}
              </button>
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Country</label>
            <select
              value={country}
              onChange={e => setCountry(e.target.value)}
              style={styles.select}
            >
              <option value="USA">United States</option>
              <option value="ISR">Israel</option>
              <option value="CAN">Canada</option>
              <option value="GBR">United Kingdom</option>
              <option value="AUS">Australia</option>
              <option value="ZAF">South Africa</option>
              <option value="FRA">France</option>
              <option value="BRA">Brazil</option>
              <option value="ARG">Argentina</option>
              <option value="RUS">Russia</option>
              <option value="MEX">Mexico</option>
              <option value="GER">Germany</option>
              <option value="NLD">Netherlands</option>
              <option value="BEL">Belgium</option>
              <option value="ESP">Spain</option>
              <option value="ITA">Italy</option>
              <option value="CHL">Chile</option>
              <option value="UKR">Ukraine</option>
              <option value="HUN">Hungary</option>
              <option value="AUT">Austria</option>
            </select>
          </div>

          <div style={styles.gridTwo}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Latitude</label>
              <input
                type="text"
                value={latitude}
                onChange={e => setLatitude(e.target.value)}
                placeholder="Auto-populated"
                style={styles.input}
              />



            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Longitude</label>
              <input
                type="text"
                value={longitude}
                onChange={e => setLongitude(e.target.value)}
                placeholder="Auto-populated"
                style={styles.input}
              />
            </div>
          </div>

          <hr style={styles.sectionDivider} />

          <h3 style={styles.subsectionTitle}>Regional Preferences</h3>

          <div style={styles.formGroup}>
            <label style={styles.label}>Display Language</label>
            <select
              value={language}
              onChange={e => setLanguage(e.target.value)}
              style={styles.select}
            >
              <option value="he">Hebrew</option>
              <option value="en">English</option>
              <option value="s">Sephardic Transliteration</option>
              <option value="a">Ashkenazic Transliteration</option>
            </select>
          </div>

          <div style={styles.gridTwo}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Time Format</label>
              <select
                value={timeFormat}
                onChange={e => setTimeFormat(e.target.value)}
                style={styles.select}
              >
                <option value="12h">12-hour (e.g., 7:30 PM)</option>
                <option value="24h">24-hour (e.g., 19:30)</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Show AM/PM (12h only)</label>
              <select
                value={showAmPm ? 'yes' : 'no'}
                onChange={e => setShowAmPm(e.target.value === 'yes')}
                style={styles.select}
                disabled={timeFormat === '24h'}
              >
                <option value="no">No (e.g., 7:30)</option>
                <option value="yes">Yes (e.g., 7:30 PM)</option>
              </select>
            </div>
          </div>
          <div style={styles.gridTwo}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Show Seconds</label>
              <select
                value={showSeconds ? 'yes' : 'no'}
                onChange={e => setShowSeconds(e.target.value === 'yes')}
                style={styles.select}
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </div>
          </div>

          <hr style={styles.sectionDivider} />

          <h3 style={styles.subsectionTitle}>PDF Export Logo</h3>
          <LogoUpload />
        </div>

        <div style={{
          padding: '2.5rem',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          {successMessage && (
            <div style={{
              ...styles.alert,
              ...(successMessage.includes('✓') || successMessage.includes('success') ? styles.alertSuccess : styles.alertError),
              marginTop: '0',
              marginBottom: '1.5rem'
            }}>
              {successMessage.includes('recalculating') && (
                <span style={{
                  display: 'inline-block',
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(0,0,0,0.1)',
                  borderTopColor: '#059669',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginRight: '8px',
                  verticalAlign: 'middle'
                }}></span>
              )}
              {successMessage}
            </div>
          )}
          <button
            type="submit"
            disabled={isSaving}
            style={{
              ...styles.primaryButton,
              ...(isSaving ? { backgroundColor: '#9ca3af', cursor: 'not-allowed', boxShadow: 'none' } : {})
            }}
          >
          {isSaving ? (
            <>
              <div style={styles.spinner}></div>
              Saving Settings...
            </>
          ) : (
            'Save Configuration'
          )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ZipcodeForm;